import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Button,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import { ExpandMore, ExpandLess, CreditCard, AccountBalance, Payment } from '@mui/icons-material';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardAPI, transactionsAPI, categoriesAPI, banksAPI, Bank } from '../services/api';

interface Summary {
  monthly: {
    income: number;
    expenses: number;
    balance: number;
  };
  yearly: {
    income: number;
    expenses: number;
    balance: number;
  };
  bank_balances: Array<{
    bank_id: number;
    bank_name: string;
    credit_card: boolean;
    income: number;
    expenses: number;
    transfers_out: number;
    transfers_in: number;
    balance: number;
  }>;
  bank_last_transactions: Array<{
    bank_id: number;
    bank_name: string;
    credit_card: boolean;
    last_transaction_date: string | null;
    last_transaction_amount: number | null;
    last_transaction_description: string | null;
  }>;
  planned_expenses: {
    summary: {
      total_active: number;
      monthly_total: number;
      yearly_total: number;
      upcoming_count: number;
      overdue_count: number;
    };
    upcoming_expenses: Array<{
      id: number;
      title: string;
      amount: number;
      monthly_amount: number;
      next_due_date: string;
      days_until_due: number;
      expense_type: string;
      category_name: string;
    }>;
    overdue_expenses: Array<{
      id: number;
      title: string;
      amount: number;
      monthly_amount: number;
      next_due_date: string;
      days_overdue: number;
      expense_type: string;
      category_name: string;
    }>;
  };
  period: {
    start_date: string;
    end_date: string;
    year: number;
    is_all_time: boolean;
  };
}

interface ChartData {
  expense_by_category: Array<{ name: string; value: number }>;
  monthly_trend: Array<{
    month: string;
    income: number;
    expenses: number;
    balance: number;
  }>;
  recent_transactions: Array<{
    id: number;
    amount: number;
    description: string;
    date: string;
    transaction_type: 'income' | 'expense' | 'transfer';
    category: { name: string; color: string };
    bank: { name: string };
    to_bank?: { name: string };
    to_bank_name?: string;
  }>;
}

interface Transaction {
  id: number;
  amount: number;
  description: string;
  date: string;
  transaction_type: 'income' | 'expense';
  category: { id: number; name: string; color: string };
  bank: { id: number; name: string };
  to_bank?: { id: number; name: string };
  to_bank_name?: string;
  reference_number?: string;
}

const DashboardPage: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState<'range' | 'all'>('all');
  const [bankBalanceExpanded, setBankBalanceExpanded] = useState(false);
  const [plannedExpensesExpanded, setPlannedExpensesExpanded] = useState(false);
  
  // Transaction dialog state
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [categories, setCategories] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [transactionFormData, setTransactionFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().slice(0, 16),
    transaction_type: 'expense' as 'income' | 'expense',
    category_id: '',
    bank_id: '',
    to_bank_id: '',
    to_bank_name: '',
    is_transfer: false,
    reference_number: '',
  });


  // Quick date range presets
  const quickRanges = [
    { label: 'Today', start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] },
    { label: 'This Week', start: getWeekStart(), end: new Date().toISOString().split('T')[0] },
    { label: 'This Month', start: getMonthStart(), end: new Date().toISOString().split('T')[0] },
    { label: 'Last 30 Days', start: getDaysAgo(30), end: new Date().toISOString().split('T')[0] },
    { label: 'Last 90 Days', start: getDaysAgo(90), end: new Date().toISOString().split('T')[0] },
  ];

  function getWeekStart() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek;
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  }

  function getMonthStart() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  }

  function getDaysAgo(days: number) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate, filterType]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      let params: any = {};
      
      if (filterType === 'range' && startDate && endDate) {
        params = { start_date: startDate, end_date: endDate };
      } else {
        // For all time view, don't send any date parameters
        params = {};
      }
      
      const [summaryRes, chartsRes] = await Promise.all([
        dashboardAPI.getSummary({ ...params, approved: true }),
        dashboardAPI.getCharts({ ...params, approved: true }),
      ]);
      console.log('Dashboard API Response:', summaryRes.data);
      setSummary(summaryRes.data);
      setChartData(chartsRes.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRange = (range: { start: string; end: string }) => {
    setStartDate(range.start);
    setEndDate(range.end);
    setFilterType('range');
  };

  const formatCurrency = (value: any): string => {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return '₹0.00';
    
    // Use JavaScript's built-in Indian number formatting
    return `₹${num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Transaction dialog functions
  const handleAddTransaction = async () => {
    try {
      // Fetch categories and banks
      const [categoriesRes, banksRes] = await Promise.all([
        categoriesAPI.getAll(),
        banksAPI.getAll()
      ]);
      
      // Ensure we have arrays
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setBanks(Array.isArray(banksRes.data) ? banksRes.data : []);
      
      // Reset form data
      setTransactionFormData({
        amount: '',
        description: '',
        date: new Date().toISOString().slice(0, 16),
        transaction_type: 'expense',
        category_id: '',
        bank_id: '',
        to_bank_id: '',
        to_bank_name: '',
        is_transfer: false,
        reference_number: '',
      });
      
      setEditingTransaction(null);
      setTransactionDialogOpen(true);
    } catch (error: any) {
      console.error('Error loading transaction form data:', error);
      setError(error.response?.data?.error || 'Failed to load transaction form data');
    }
  };

  const handleTransactionSubmit = async () => {
    try {
      const submitData = {
        ...transactionFormData,
        amount: parseFloat(transactionFormData.amount),
        category_id: parseInt(transactionFormData.category_id),
        bank_id: parseInt(transactionFormData.bank_id),
        to_bank_id: transactionFormData.to_bank_id ? parseInt(transactionFormData.to_bank_id) : undefined,
      };

      if (editingTransaction) {
        await transactionsAPI.update(editingTransaction.id, submitData);
      } else {
        await transactionsAPI.create(submitData);
      }

      setTransactionDialogOpen(false);
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save transaction');
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'success';
      case 'expense':
        return 'error';
      case 'transfer':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'income':
        return '+';
      case 'expense':
        return '-';
      case 'transfer':
        return '↔';
      default:
        return '';
    }
  };

  const getTransferDestination = (transaction: any) => {
    if (transaction.to_bank) {
      return transaction.to_bank.name;
    } else if (transaction.to_bank_name) {
      return transaction.to_bank_name;
    }
    return '';
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // Utility function to safely handle arrays that might be frozen
  const safeArray = <T,>(arr: T[] | undefined | null): T[] => {
    if (!arr) return [];
    try {
      return Array.from(arr);
    } catch (error) {
      console.warn('Failed to create array copy, using original:', error);
      return arr;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!summary || !chartData) {
    return <Alert severity="error">Failed to load dashboard data</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dashboard</Typography>
        
        <Box display="flex" gap={2} alignItems="center">
          {/* Add Transaction Icon - Small Icon Like FA */}
          
          {/* Date Range Filters */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter Type</InputLabel>
            <Select
              value={filterType}
              label="Filter Type"
                              onChange={(e) => setFilterType(e.target.value as 'range' | 'all')}
            >
              <MenuItem value="range">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          
          {filterType === 'range' ? (
            <>
              <TextField
                size="small"
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : (
            <TextField
              size="small"
              label="Month"
              type="month"
              value={startDate.substring(0, 7)}
              onChange={(e) => setStartDate(e.target.value + '-01')}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Box>
      </Box>

      {/* Quick Range Buttons */}
      {filterType === 'range' && (
        <Box display="flex" gap={1} mb={3} flexWrap="wrap">
          {quickRanges.map((range) => (
            <Button
              key={range.label}
              variant="outlined"
              size="small"
              onClick={() => handleQuickRange(range)}
            >
              {range.label}
            </Button>
          ))}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Alternative: Floating Action Button Style (uncomment to use instead of the button above) */}
      {/* 
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
        <Button
          variant="contained"
          onClick={handleAddTransaction}
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
            boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)',
            '&:hover': {
              background: 'linear-gradient(45deg, #FF5252 30%, #FF7043 90%)',
              boxShadow: '0 8px 25px rgba(255, 107, 107, 0.6)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <Add sx={{ fontSize: 32, color: 'white' }} />
        </Button>
      </Box>
      */}

      {/* Period Display */}
      <Box mb={3} display="flex">
        <Typography variant="h6" color="textSecondary">
          {summary.period.is_all_time ? 'All Time' : `Period: ${new Date(summary.period.start_date).toLocaleDateString()} - ${new Date(summary.period.end_date).toLocaleDateString()}`}
        </Typography>&nbsp;&nbsp;
        <IconButton
          onClick={handleAddTransaction}
          sx={{ 
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.5)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Payment sx={{ fontSize: 20, color: 'white' }} />
        </IconButton>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Period Income
              </Typography>
              <Typography variant="h4" color="success.main">
                {formatCurrency(summary.monthly.income)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Period Expenses
              </Typography>
              <Typography variant="h4" color="error.main">
                {formatCurrency(summary.monthly.expenses)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Period Balance
              </Typography>
              <Typography variant="h4" color={summary.monthly.balance >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summary.monthly.balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Yearly Balance ({summary.period.year})
              </Typography>
              <Typography variant="h4" color={summary.yearly.balance >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summary.yearly.balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Planned Expenses Summary */}
      {summary.planned_expenses && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6">
                      Planned Expenses Summary
                    </Typography>
                    {!plannedExpensesExpanded && (
                      <Box>
                        <Typography variant="h4" color="warning.main" sx={{ mt: 1 }}>
                          ₹{(() => {
                            try {
                              const monthlyTotal = summary.planned_expenses?.summary?.monthly_total;
                              // Convert string to number if needed
                              let numericValue = monthlyTotal;
                              if (typeof monthlyTotal === 'string') {
                                numericValue = parseFloat(monthlyTotal);
                              }
                              
                              if (typeof numericValue === 'number' && !isNaN(numericValue) && isFinite(numericValue)) {
                                return numericValue.toFixed(2);
                              }
                              return '0.00';
                            } catch (error) {
                              console.error('Error formatting monthly total:', error);
                              return '0.00';
                            }
                          })()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Click to expand and view detailed breakdown
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={() => setPlannedExpensesExpanded(!plannedExpensesExpanded)}
                    size="small"
                  >
                    {plannedExpensesExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={plannedExpensesExpanded}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="primary.main">
                        {(() => {
                          try {
                            const totalActive = summary.planned_expenses?.summary?.total_active;
                            return typeof totalActive === 'number' && !isNaN(totalActive) ? totalActive : 0;
                          } catch (error) {
                            console.error('Error getting total active:', error);
                            return 0;
                          }
                        })()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Expenses
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="warning.main">
                        ₹{(() => {
                          try {
                            const monthlyTotal = summary.planned_expenses?.summary?.monthly_total;
                            console.log('Dashboard - Monthly Total Debug:', {
                              monthlyTotal,
                              type: typeof monthlyTotal,
                              isNaN: isNaN(monthlyTotal),
                              isFinite: isFinite(monthlyTotal),
                              fullSummary: summary.planned_expenses?.summary
                            });
                            
                            // Convert string to number if needed
                            let numericValue = monthlyTotal;
                            if (typeof monthlyTotal === 'string') {
                              numericValue = parseFloat(monthlyTotal);
                            }
                            
                            if (typeof numericValue === 'number' && !isNaN(numericValue) && isFinite(numericValue)) {
                              return numericValue.toFixed(2);
                            }
                            return '0.00';
                          } catch (error) {
                            console.error('Error formatting monthly total:', error);
                            return '0.00';
                          }
                        })()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Monthly Total
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="info.main">
                        {(() => {
                          try {
                            const upcomingCount = summary.planned_expenses?.summary?.upcoming_count;
                            return typeof upcomingCount === 'number' && !isNaN(upcomingCount) ? upcomingCount : 0;
                          } catch (error) {
                            console.error('Error getting upcoming count:', error);
                            return 0;
                          }
                        })()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Upcoming
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="error.main">
                        {(() => {
                          try {
                            const overdueCount = summary.planned_expenses?.summary?.overdue_count;
                            return typeof overdueCount === 'number' && !isNaN(overdueCount) ? overdueCount : 0;
                          } catch (error) {
                            console.error('Error getting overdue count:', error);
                            return 0;
                          }
                        })()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Overdue
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Upcoming and Overdue Expenses */}
                <Box mt={3}>
                  <Grid container spacing={3}>
                    {/* Upcoming Expenses */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom color="info.main">
                        📅 Upcoming Expenses
                      </Typography>
                      {summary.planned_expenses?.upcoming_expenses && summary.planned_expenses.upcoming_expenses.length > 0 ? (
                        summary.planned_expenses.upcoming_expenses.map((expense) => (
                          <Box key={expense.id} p={2} mb={1} bgcolor="info.50" borderRadius={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="subtitle2">{expense.title}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {expense.category_name} • {expense.expense_type.replace('_', ' ')}
                                </Typography>
                              </Box>
                              <Box textAlign="right">
                                <Typography variant="body2" fontWeight="bold">
                                  ₹{(() => {
                                    try {
                                      const monthlyAmount = typeof expense.monthly_amount === 'string' ? parseFloat(expense.monthly_amount) || 0 : (expense.monthly_amount || 0);
                                      if (monthlyAmount > 0) {
                                        return monthlyAmount.toFixed(2);
                                      }
                                      // Fallback to base amount if monthly_amount is 0
                                      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : (expense.amount || 0);
                                      if (amount > 0) {
                                        return amount.toFixed(2);
                                      }
                                      return '0.00';
                                    } catch (error) {
                                      console.error('Error formatting expense amount:', error);
                                      return '0.00';
                                    }
                                  })()}
                                </Typography>
                                <Typography variant="caption" color="info.main">
                                  {expense.days_until_due} days left
                                </Typography>

                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No upcoming expenses
                        </Typography>
                      )}
                    </Grid>

                    {/* Overdue Expenses */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom color="error.main">
                        ⚠️ Overdue Expenses
                      </Typography>
                      {summary.planned_expenses?.overdue_expenses && summary.planned_expenses.overdue_expenses.length > 0 ? (
                        summary.planned_expenses.overdue_expenses.map((expense) => (
                          <Box key={expense.id} p={2} mb={1} bgcolor="error.50" borderRadius={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="subtitle2">{expense.title}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {expense.category_name} • {expense.expense_type.replace('_', ' ')}
                                </Typography>
                              </Box>
                              <Box textAlign="right">
                                <Typography variant="body2" fontWeight="bold">
                                  ₹{(() => {
                                    try {
                                      const monthlyAmount = typeof expense.monthly_amount === 'string' ? parseFloat(expense.monthly_amount) || 0 : (expense.monthly_amount || 0);
                                      if (monthlyAmount > 0) {
                                        return monthlyAmount.toFixed(2);
                                      }
                                      // Fallback to base amount if monthly_amount is 0
                                      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : (expense.amount || 0);
                                      if (amount > 0) {
                                        return amount.toFixed(2);
                                      }
                                      return '0.00';
                                    } catch (error) {
                                      console.error('Error formatting expense amount:', error);
                                      return '0.00';
                                    }
                                  })()}
                                </Typography>
                                                                 <Typography variant="caption" color="error.main">
                                   {Math.abs(expense.days_overdue || 0)} days overdue
                                 </Typography>

                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No overdue expenses
                        </Typography>
                        )}
                    </Grid>
                  </Grid>
                </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bank Balances - Collapsible */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Bank Balances ({summary.bank_balances.length} banks)
                </Typography>
                <IconButton
                  onClick={() => setBankBalanceExpanded(!bankBalanceExpanded)}
                  size="small"
                >
                  {bankBalanceExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={bankBalanceExpanded}>
                <Grid container spacing={2}>
                  {summary.bank_balances.map((bank) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={bank.bank_id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={1}>
                            {bank.credit_card ? (
                              <CreditCard sx={{ mr: 1, color: 'secondary.main', fontSize: 20 }} />
                            ) : (
                              <AccountBalance sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                            )}
                            <Typography variant="subtitle2">
                              {bank.bank_name}
                            </Typography>
                            {bank.credit_card && (
                              <Chip 
                                label="Credit Card" 
                                size="small" 
                                color="secondary" 
                                sx={{ ml: 1, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="h6" color={bank.balance >= 0 ? 'success.main' : 'error.main'}>
                            {formatCurrency(bank.balance)}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="caption" display="block" color="success.main">
                              +{formatCurrency(bank.income)}
                            </Typography>
                            <Typography variant="caption" display="block" color="error.main">
                              -{formatCurrency(bank.expenses)}
                            </Typography>
                            {bank.transfers_in > 0 && (
                              <Typography variant="caption" display="block" color="info.main">
                                ↙ +{formatCurrency(bank.transfers_in)}
                              </Typography>
                            )}
                            {bank.transfers_out > 0 && (
                              <Typography variant="caption" display="block" color="info.main">
                                ↗ -{formatCurrency(bank.transfers_out)}
                              </Typography>
                            )}
                            
                            {/* Last Transaction Date */}
                            {summary.bank_last_transactions && (() => {
                              const lastTransaction = summary.bank_last_transactions.find(lt => lt.bank_id === bank.bank_id);
                              return lastTransaction?.last_transaction_date ? (
                                <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                                  📅 Last: {new Date(lastTransaction.last_transaction_date).toLocaleDateString()}
                                  {lastTransaction.last_transaction_amount && (
                                    <span style={{ marginLeft: '8px' }}>
                                      ({formatCurrency(lastTransaction.last_transaction_amount)})
                                    </span>
                                  )}
                                </Typography>
                              ) : null;
                            })()}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
              
              {!bankBalanceExpanded && (
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                  <Typography variant="body2" color="textSecondary">
                    Click to expand and view detailed bank balances
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(summary.bank_balances.reduce((sum, bank) => sum + bank.balance, 0))}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>



      {/* Charts */}
      <Grid container spacing={3}>
        {/* Expense by Category Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Expenses by Category
              </Typography>
              {chartData.expense_by_category && chartData.expense_by_category.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.expense_by_category}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {safeArray(chartData.expense_by_category).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="textSecondary">
                    No expense data available for this period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Trend Line Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Trend ({summary.period.year})
              </Typography>
              {chartData.monthly_trend && chartData.monthly_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="income" stroke="#4CAF50" name="Income" />
                    <Line type="monotone" dataKey="expenses" stroke="#F44336" name="Expenses" />
                    <Line type="monotone" dataKey="balance" stroke="#2196F3" name="Balance" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="textSecondary">
                    No trend data available for this period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expenses by Category List */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Expenses by Category
              </Typography>
              {chartData.expense_by_category && chartData.expense_by_category.length > 0 ? (
                <Box>
                  {safeArray(chartData.expense_by_category)
                    .sort((a, b) => b.value - a.value) // Sort by amount descending
                    .map((category, index) => (
                      <Box
                        key={category.name}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1}
                        borderBottom={index < chartData.expense_by_category.length - 1 ? '1px solid #eee' : 'none'}
                      >
                        <Typography variant="body2" color="textPrimary">
                          {category.name}
                        </Typography>
                        <Typography variant="body2" color="error.main" fontWeight="medium">
                          {formatCurrency(category.value)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <Typography color="textSecondary">
                    No expense data available for this period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Transactions
              </Typography>
              {chartData.recent_transactions && chartData.recent_transactions.length > 0 ? (
                <Box>
                  {safeArray(chartData.recent_transactions).map((transaction) => (
                    <Box
                      key={transaction.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      borderBottom="1px solid #eee"
                    >
                      <Box>
                        <Typography variant="body2">
                          {transaction.description}
                          {transaction.transaction_type === 'transfer' && (
                            <Typography component="span" variant="caption" color="textSecondary" ml={1}>
                              → {getTransferDestination(transaction)}
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(transaction.date).toLocaleString()} • {transaction.bank.name} • {transaction.category.name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={transaction.transaction_type}
                          size="small"
                          color={getTransactionTypeColor(transaction.transaction_type) as 'success' | 'error' | 'info' | 'default'}
                        />
                        <Typography
                          variant="body2"
                          color={getTransactionTypeColor(transaction.transaction_type)}
                          fontWeight="bold"
                        >
                          {getTransactionTypeIcon(transaction.transaction_type)}{formatCurrency(transaction.amount)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <Typography color="textSecondary">
                    No recent transactions available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Transaction Dialog */}
      <Dialog open={transactionDialogOpen} onClose={() => setTransactionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Bank */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bank</InputLabel>
                <Select
                  value={transactionFormData.bank_id}
                  label="Bank"
                  onChange={(e) => setTransactionFormData({ ...transactionFormData, bank_id: e.target.value })}
                >
                  {banks && banks.length > 0 ? banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  )) : (
                    <MenuItem disabled>Loading banks...</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Transaction Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={transactionFormData.transaction_type}
                  label="Type"
                  onChange={(e) => setTransactionFormData({ ...transactionFormData, transaction_type: e.target.value as 'income' | 'expense' })}
                >
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={transactionFormData.amount}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, amount: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={transactionFormData.category_id}
                  label="Category"
                  onChange={(e) => setTransactionFormData({ ...transactionFormData, category_id: e.target.value })}
                >
                  {categories && categories.length > 0 ? categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  )) : (
                    <MenuItem disabled>Loading categories...</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="datetime-local"
                value={transactionFormData.date}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={transactionFormData.description}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number (Optional)"
                value={transactionFormData.reference_number}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, reference_number: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTransactionSubmit} variant="contained">
            {editingTransaction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
